﻿/**
 * Audit Evaluation Integration Layer
 * Connects SDK Service to Zustand Store
 */
import i18next from 'i18next';
import { toast } from 'sonner';
import { useAuditEvaluationStore } from '../../store/audit-evaluation.store.js';
import { useTenantStore } from '../../store/tenant.store.js';
import { useAuthStore } from '../../store/auth.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import {
    isControlAuditorEvaluationComplete,
    normalizeControlAnswersForSave,
} from '../../constants/audit-control-auditor-fields.js';
import { toUploadProgressHandler } from '@/lib/upload/percent-from-axios-progress.js';
import { Logger } from '@/lib/app-log.js';

const debugLog = Logger.debug;

export class AuditEvaluationIntegration {
    constructor(auditAssignmentService, assessmentService, assessmentQuestionService) {
        this.auditAssignment = auditAssignmentService;
        this.assessment = assessmentService;
        this.assessmentQuestion = assessmentQuestionService;
    }

    _getCurrentUserId() {
        const user = useAuthStore.getState().user;
        if (!user) return '';
        return String(user._id || user.id || '');
    }

    _normalizeEvidence(rawEvidence) {
        if (!rawEvidence || typeof rawEvidence !== 'object') return null;
        const safeStoredName = typeof rawEvidence.storedName === 'string' ? rawEvidence.storedName.trim() : '';
        if (!safeStoredName) return null;
        const safeFileName = typeof rawEvidence.fileName === 'string' && rawEvidence.fileName.trim()
            ? rawEvidence.fileName.trim()
            : safeStoredName;
        const safeId = rawEvidence.id || rawEvidence._id || `ev-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        return {
            id: String(safeId),
            fileName: safeFileName,
            storedName: safeStoredName,
            filePath: typeof rawEvidence.filePath === 'string' ? rawEvidence.filePath : null,
            fileUrl: typeof rawEvidence.fileUrl === 'string' ? rawEvidence.fileUrl : null,
            mimeType: typeof rawEvidence.mimeType === 'string' ? rawEvidence.mimeType : 'application/octet-stream',
            size: Number(rawEvidence.size || 0),
            uploadedAt: rawEvidence.uploadedAt || new Date().toISOString(),
            uploadedBy: rawEvidence.uploadedBy || null
        };
    }

    _splitEvaluationKey(key) {
        const raw = String(key || '').trim();
        if (!raw) return { controlId: '', unitName: '' };
        const [controlId, ...rest] = raw.split('::');
        return {
            controlId: String(controlId || '').trim(),
            unitName: rest.length > 0 ? rest.join('::').trim() : ''
        };
    }

    _ensureDownloadableBlob(candidate) {
        if (!(candidate instanceof Blob)) return null;
        if (candidate.size <= 0) return null;
        return candidate;
    }

    _buildEvaluationUnits(controls, controlUnitMap) {
        const baseControls = Array.isArray(controls) ? controls : [];
        return baseControls.flatMap((ctrl) => {
            const controlId = String(ctrl?._id?.toString?.() || ctrl?._id || '');
            if (!controlId) return [];
            const units = controlUnitMap?.[controlId] || ctrl?.units || [];
            const uniqueUnits = [...new Set((units || []).map((d) => {
                if (typeof d === 'string') return d.trim();
                if (d && typeof d === 'object') {
                    return String(d.name || d.unitName || d.title || d.label || '').trim();
                }
                return '';
            }).filter(Boolean))];
            if (uniqueUnits.length === 0) {
                return [{ ...ctrl, baseControlId: controlId, evaluationKey: controlId, unitName: '' }];
            }
            return uniqueUnits.map((unitName) => ({
                ...ctrl,
                baseControlId: controlId,
                unitName,
                evaluationKey: `${controlId}::${unitName}`
            }));
        });
    }

    _invertUnitControlMap(unitControlMap) {
        const out = {};
        for (const [unitName, controlIds] of Object.entries(unitControlMap || {})) {
            const unit = String(unitName || '').trim();
            if (!unit) continue;
            for (const controlIdRaw of (controlIds || [])) {
                const controlId = String(controlIdRaw || '').trim();
                if (!controlId) continue;
                if (!out[controlId]) out[controlId] = [];
                if (!out[controlId].includes(unit)) out[controlId].push(unit);
            }
        }
        return out;
    }

    /**
     * Restrict control answer keys to the current auditor's assignment (multi-auditor assessments).
     */
    _filterControlAnswersForMyAssignment(controlAnswers, allEvaluations, userId) {
        if (!Array.isArray(allEvaluations) || allEvaluations.length === 0) {
            return controlAnswers || {};
        }
        if (!userId) return controlAnswers || {};
        const myEval = allEvaluations.find((ev) => String(ev.auditorId) === String(userId));
        if (!myEval) return controlAnswers || {};
        const allowed = new Set((myEval.controlIds || []).map((id) => String(id)));
        if (allowed.size === 0) return {};
        const out = {};
        for (const [k, v] of Object.entries(controlAnswers || {})) {
            const { controlId } = this._splitEvaluationKey(k);
            if (allowed.has(controlId || String(k))) out[k] = v;
        }
        return out;
    }

    _controlsForMyAssignment(controls, allEvaluations, userId) {
        const list = controls || [];
        if (!Array.isArray(allEvaluations) || allEvaluations.length === 0 || !userId) {
            return list;
        }
        const myEval = allEvaluations.find((ev) => String(ev.auditorId) === String(userId));
        const allowed = new Set((myEval?.controlIds || []).map((id) => String(id)));
        if (allowed.size === 0) return list;
        return list.filter((c) => {
            const controlId = String(c.baseControlId || c._id?.toString?.() || c._id);
            return allowed.has(controlId);
        });
    }

    /**
     * Fetch My Audits - gets audit assignments for the current logged-in user
     * Backend uses req.userId from JWT token, no need to pass username
     */
    async fetchMyAudits(params = {}) {
        const store = useAuditEvaluationStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            const message = getIntegrationErrorMessage({ message: 'Tenant context required' });
            return { success: false, error: message };
        }

        try {
            const response = await this.auditAssignment.getMyAudits({
                tenantId,
                search: params.search !== undefined ? params.search : store.searchQuery,
                sortBy: params.sortBy !== undefined ? params.sortBy : store.sortBy,
                sortDirection: params.sortDirection !== undefined ? params.sortDirection : store.sortDirection,
                page: params.page || store.pagination.currentPage || 1,
                pageSize: params.pageSize || store.pagination.itemsPerPage || 10
            });

            const rawData = response.data || [];

            const mappedData = rawData.map(assignment => {
                const assessment = assignment.assessment || {};
                const canEvaluate = assignment.canEvaluate !== false;
                return {
                    id: assignment._id || assignment.id,
                    _id: assignment._id || assignment.id,
                    assignmentId: canEvaluate ? (assignment._id || assignment.id) : undefined,
                    assessmentId: assessment._id || assessment.id,
                    title: assessment.title || '-',
                    teamLeader: assessment.teamLeader,
                    startDate: assessment.startDate || '-',
                    endDate: assessment.endDate || '-',
                    status: assessment.status || 'Planned',
                    role: assignment.role,
                    assignmentStatus: assignment.status,
                    canEvaluate,
                    units: assessment.units || [],
                    questionDocumentIds: assignment.questionDocumentIds || [],
                    controlIds: assignment.controlIds || [],
                    criteria: assessment.criteria || [],
                    purpose: assessment.purpose || ''
                };
            });

            store.setMyAudits(mappedData);

            // Handle Standard Pagination
            if (response.pagination) {
                const p = response.pagination;
                if (store.setPagination) {
                    store.setPagination({
                        currentPage: p.page,
                        itemsPerPage: p.pageSize,
                        totalItems: p.total,
                        totalPages: p.totalPages,
                        hasNextPage: p.page < p.totalPages,
                        hasPreviousPage: p.page > 1
                    });
                }
            }

            // Handle Unique Values
            if (response.uniqueValues && store.setUniqueValues) {
                store.setUniqueValues(response.uniqueValues);
            }

            return { success: true, data: mappedData };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (store.setError) store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Open Evaluation dialog
     * Fetches the question documents and current evaluation state
     */
    async openEvaluation({ audit }) {
        const store = useAuditEvaluationStore.getState();
        store.setEvaluationOpen(true);
        store.setEvaluationLoading(true);
        store.clearError();
        store.setCurrentAudit(audit);

        const assessmentId = audit?.assessmentId || audit?.id;
        const hasControls = (audit?.controlIds || []).length > 0;

        try {
            if (hasControls) {
                const [controlsRes, evaluationRes] = await Promise.all([
                    this.assessment.getAuditControls(assessmentId),
                    this.assessment.getEvaluation(assessmentId),
                ]);

                store.setControls(controlsRes.data?.controls || []);
                store.setControlAnswers(evaluationRes.data?.controlAnswers || {});
                store.setEvaluation(evaluationRes.data);
                store.setQuestionDocs([]);
            } else {
                const ids = audit?.questionDocumentIds || [];
                const [questionDocsRes, evaluationRes] = await Promise.all([
                    ids.length > 0
                        ? this.assessmentQuestion.getByIds(ids)
                        : Promise.resolve({ data: [] }),
                    assessmentId
                        ? this.assessment.getEvaluation(assessmentId)
                        : Promise.resolve({ data: { answers: {}, status: 'Draft' } }),
                ]);

                store.setQuestionDocs(questionDocsRes.data || []);
                store.setEvaluation(evaluationRes.data);
                store.setControls([]);
                store.setControlAnswers({});
            }

            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            store.setQuestionDocs([]);
            store.setControls([]);
            store.setControlAnswers({});
            store.setEvaluation(null);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setEvaluationLoading(false);
        }
    }

    /**
     * Save Draft
     */
    async saveDraft({ assessmentId, silent = false }) {
        const store = useAuditEvaluationStore.getState();
        if (store.currentAudit?.canEvaluate === false) {
            return { success: false, error: 'Read-only team view' };
        }
        if (!silent) {
            store.setEvaluationLoading(true);
        }
        store.clearError();

        try {
            const userId = this._getCurrentUserId();
            const allEvaluations = store.allEvaluations || [];
            const answers = store.evaluation?.answers || {};
            const rawControls = store.controlAnswers || {};
            const filteredControls = this._filterControlAnswersForMyAssignment(
                rawControls,
                allEvaluations,
                userId
            );
            const controlAnswers = normalizeControlAnswersForSave(filteredControls);

            const response = await this.assessment.submitDraft(assessmentId, {
                answers,
                controlAnswers,
            });

            store.setEvaluation(response.data);
            if (response.data?.controlAnswers) {
                store.setControlAnswers(response.data.controlAnswers);
            }
            toast.success(i18next.t('integrationToasts:draftSaved', { defaultValue: 'Draft saved' }));

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (!silent) {
                store.setEvaluationLoading(false);
            }
        }
    }

    async submit({ assessmentId }) {
        const store = useAuditEvaluationStore.getState();
        if (store.currentAudit?.canEvaluate === false) {
            return { success: false, error: 'Read-only team view' };
        }
        store.setEvaluationLoading(true);
        store.clearError();

        try {
            const userId = this._getCurrentUserId();
            const allEvaluations = store.allEvaluations || [];
            const controls = this._controlsForMyAssignment(
                store.controls || [],
                allEvaluations,
                userId
            );
            const rawControlAnswers = store.controlAnswers || {};
            if (controls.length > 0) {
                const incomplete = controls.filter((c) => {
                    const id = c.evaluationKey || c._id?.toString?.() || c._id;
                    return !isControlAuditorEvaluationComplete(rawControlAnswers[id]);
                });
                if (incomplete.length > 0) {
                    toast.error(i18next.t('integrationToasts:fillInTheRequiredAuditorFieldsForAllControls', { defaultValue: 'Fill in the required auditor fields for all controls.' }));
                    return { success: false, error: 'Incomplete control evaluations' };
                }
            }

            const answers = store.evaluation?.answers || {};
            const filteredControls = this._filterControlAnswersForMyAssignment(
                rawControlAnswers,
                store.allEvaluations || [],
                userId
            );
            const controlAnswers = normalizeControlAnswersForSave(filteredControls);
            const response = await this.assessment.submitEvaluation(assessmentId, {
                answers,
                controlAnswers,
            });

            store.setEvaluation(response.data);
            if (response.data?.controlAnswers) {
                store.setControlAnswers(response.data.controlAnswers);
            }
            toast.success(i18next.t('integrationToasts:evaluationSubmitted', { defaultValue: 'Evaluation submitted' }));

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setEvaluationLoading(false);
        }
    }

    /**
     * Upload evidence (question-based / legacy assignment path).
     * @param {string} assignmentId
     * @param {File} file
     * @param {Function} [onProgress]
     */
    async uploadEvidence(assignmentId, file, onProgress) {
        try {
            const response = await this.auditAssignment.uploadEvidence(assignmentId, file, {
                onUploadProgress: toUploadProgressHandler(onProgress),
            });
            const normalized = this._normalizeEvidence(response.data);
            if (!normalized) {
                return { success: false, error: 'Geçersiz kanıt yanıtı' };
            }
            return { success: true, data: normalized };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Upload control evidence (shared assessment path; visible to all assigned auditors).
     */
    async uploadControlEvidence(assessmentId, controlId, file, onProgress) {
        try {
            debugLog('[AuditEvaluationIntegration] uploadControlEvidence start', {
                assessmentId,
                controlId,
                fileName: file?.name,
                fileSize: file?.size,
                fileType: file?.type,
            });
            const response = await this.assessment.uploadControlEvidence(assessmentId, controlId, file, {
                onUploadProgress: toUploadProgressHandler(onProgress),
            });
            const normalized = this._normalizeEvidence(response.data);
            if (!normalized) {
                debugLog('[AuditEvaluationIntegration] uploadControlEvidence invalid response', {
                    assessmentId,
                    controlId,
                    responseData: response?.data,
                });
                return { success: false, error: 'Geçersiz kanıt yanıtı' };
            }
            debugLog('[AuditEvaluationIntegration] uploadControlEvidence success', {
                assessmentId,
                controlId,
                storedName: normalized.storedName,
                fileName: normalized.fileName,
            });
            return { success: true, data: normalized };
        } catch (error) {
            debugLog('[AuditEvaluationIntegration] uploadControlEvidence error', {
                assessmentId,
                controlId,
                status: error?.statusCode || error?.status || error?.response?.status,
                message: error?.message,
                responseData: error?.response?.data || error?.details,
            });
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async deleteEvidence(assignmentId, storedName) {
        try {
            await this.auditAssignment.deleteEvidence(assignmentId, storedName);
            toast.success(i18next.t('integrationToasts:evidenceDeleted', { defaultValue: 'Evidence deleted' }));
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async deleteControlEvidence(assessmentId, controlId, storedName) {
        try {
            debugLog('[AuditEvaluationIntegration] deleteControlEvidence start', {
                assessmentId,
                controlId,
                storedName,
            });
            await this.assessment.deleteControlEvidence(assessmentId, controlId, storedName);
            debugLog('[AuditEvaluationIntegration] deleteControlEvidence success', {
                assessmentId,
                controlId,
                storedName,
            });
            toast.success(i18next.t('integrationToasts:evidenceDeleted', { defaultValue: 'Evidence deleted' }));
            return { success: true };
        } catch (error) {
            debugLog('[AuditEvaluationIntegration] deleteControlEvidence error', {
                assessmentId,
                controlId,
                storedName,
                status: error?.statusCode || error?.status || error?.response?.status,
                message: error?.message,
                responseData: error?.response?.data || error?.details,
            });
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async downloadEvidence(assignmentId, storedName, originalFileName) {
        try {
            const rawBlob = await this.auditAssignment.downloadEvidence(assignmentId, storedName);
            const blob = this._ensureDownloadableBlob(rawBlob);
            if (!blob) {
                toast.error(i18next.t('integrationToasts:couldNotDownloadFileInvalidFileContent', { defaultValue: 'Could not download file (invalid file content).' }));
                debugLog('[AuditEvaluationIntegration] downloadEvidence invalid blob', {
                    assignmentId,
                    storedName,
                    payloadType: typeof rawBlob,
                    blobSize: rawBlob?.size,
                });
                return { success: false, error: 'Could not download file (invalid file content).' };
            }
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = originalFileName || storedName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async downloadControlEvidence(assessmentId, controlId, storedName, originalFileName) {
        try {
            debugLog('[AuditEvaluationIntegration] downloadControlEvidence start', {
                assessmentId,
                controlId,
                storedName,
                originalFileName,
            });
            const rawBlob = await this.assessment.downloadControlEvidence(assessmentId, controlId, storedName);
            const blob = this._ensureDownloadableBlob(rawBlob);
            if (!blob) {
                toast.error(i18next.t('integrationToasts:couldNotDownloadFileInvalidFileContent', { defaultValue: 'Could not download file (invalid file content).' }));
                debugLog('[AuditEvaluationIntegration] downloadControlEvidence invalid blob', {
                    assessmentId,
                    controlId,
                    storedName,
                    payloadType: typeof rawBlob,
                    blobSize: rawBlob?.size,
                });
                return { success: false, error: 'Could not download file (invalid file content).' };
            }
            debugLog('[AuditEvaluationIntegration] downloadControlEvidence success', {
                assessmentId,
                controlId,
                storedName,
                blobSize: blob?.size,
                blobType: blob?.type,
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = originalFileName || storedName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            return { success: true };
        } catch (error) {
            debugLog('[AuditEvaluationIntegration] downloadControlEvidence error', {
                assessmentId,
                controlId,
                storedName,
                status: error?.statusCode || error?.status || error?.response?.status,
                message: error?.message,
                responseData: error?.response?.data || error?.details,
            });
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * @param {object} [options]
     * @param {boolean} [options.viewMode] - Read-only multi-auditor view (e.g. Assessment page): merged answers/controls. Default false = My Audits editing uses current user only.
     */
    async fetchAllEvaluations(assessmentId, audit, options = {}) {
        const store = useAuditEvaluationStore.getState();
        const viewMode = options.viewMode === true || audit?.canEvaluate === false;
        store.setEvaluationOpen(true);
        store.setEvaluationLoading(true);
        store.clearError();
        if (audit) store.setCurrentAudit(audit);

        try {
            const [allEvalsRes, controlsRes] = await Promise.all([
                this.assessment.getAllEvaluations(assessmentId),
                this.assessment.getAllAuditControls(assessmentId),
            ]);

            const evalsData = allEvalsRes.data || {};
            const allEvaluations = evalsData.evaluations || [];
            const unitList = evalsData.unitList || [];
            const unitControlMap = evalsData.unitControlMap || {};
            const controlUnitMap = evalsData.controlUnitMap
                || controlsRes.data?.controlUnitMap
                || this._invertUnitControlMap(unitControlMap);
            const controlUnits = this._buildEvaluationUnits(controlsRes.data?.controls || [], controlUnitMap);

            store.setAllEvaluations(allEvaluations);
            store.setUnitList(unitList);
            store.setUnitControlMap(unitControlMap);
            store.setSelectedUnit('all');

            const userId = this._getCurrentUserId();
            const myEval = userId
                ? allEvaluations.find((ev) => String(ev.auditorId) === userId)
                : null;

            if (viewMode) {
                store.setSelectedAuditorId('all');
                store.setControls(controlUnits);
                store.setControlAnswers(evalsData.allControlAnswers || {});
                store.setEvaluation({
                    answers: evalsData.allAnswers || {},
                    status: 'ViewAll'
                });
            } else if (myEval) {
                store.setSelectedAuditorId(userId || 'all');
                store.setControls(controlUnits);
                store.setControlAnswers(evalsData.allControlAnswers || {});
                store.setEvaluation({
                    answers: myEval.answers || {},
                    status: 'ViewAll'
                });
            } else {
                store.setSelectedAuditorId('all');
                store.setControls(controlUnits);
                store.setControlAnswers(evalsData.allControlAnswers || {});
                store.setEvaluation({
                    answers: evalsData.allAnswers || {},
                    status: 'ViewAll'
                });
            }
            store.setQuestionDocs([]);

            return { success: true, data: evalsData };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setEvaluationLoading(false);
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
